/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import {
  useInventory,
  useTokenFamilies,
  useMerchantConfig,
  usePayoutAccounts,
  useBusinessSettings,
} from "../api/hooks.js";
import { unwrap } from "../api/failure.js";
import { executeCreateOrder } from "../utils/orders.js";
import { session, currentBackendUrl } from "../stores/session.js";
import { CreateOrderScreen } from "../screens/CreateOrderScreen.js";
import { InitialLoadingState } from "../ui/InitialLoadingState.js";
import { ReadErrorBanner } from "../ui/ReadErrorBanner.js";
import { useTranslation } from "../context/translation.js";

export function CreateOrderRoute(): VNode {
  const { t } = useTranslation();
  const { products, isLoading: inventoryLoading } = useInventory();
  const {
    tokenFamilies,
    semanticDetailsComplete,
    resource: tokenFamiliesResource,
  } = useTokenFamilies();
  const { primaryCurrency, resource: configResource } = useMerchantConfig();
  const { accounts } = usePayoutAccounts();
  const { settings, resource: settingsResource } = useBusinessSettings();

  const token = session.value.token;

  if (token && settings === undefined) {
    return settingsResource.error ? (
      <ReadErrorBanner
        resource={settingsResource}
        title={t`Merchant account settings could not be loaded`}
      />
    ) : (
      <InitialLoadingState>{t`Loading…`}</InitialLoadingState>
    );
  }

  return (
    <CreateOrderScreen
      catalogueProducts={products ?? []}
      catalogueProductsLoading={inventoryLoading}
      tokenFamilies={tokenFamilies ?? []}
      semanticDetailsComplete={semanticDetailsComplete}
      tokenFamiliesResource={tokenFamiliesResource}
      configPrimaryCurrency={primaryCurrency}
      configResource={configResource}
      payoutAccounts={accounts ?? []}
      accountOrderDefaults={settings}
      onCreateOrder={
        token
          ? async (request) => {
              const res = await executeCreateOrder(
                {
                  rootUrl: new URL(currentBackendUrl.value),
                  account: session.value.account,
                },
                token,
                request,
              );
              if (res.type !== "ok") {
                const fail = res as {
                  detail?: unknown;
                  body?: unknown;
                  case?: number;
                };
                return {
                  ok: false,
                  detail: fail.detail ?? fail.body,
                  status: fail.case,
                };
              }
              return { ok: true, orderId: unwrap(res).order_id };
            }
          : undefined
      }
    />
  );
}
